import { ReactNode } from 'react';
import {
  NewVesselInsurance,
  VesselInsurance,
} from '../../../../types/insurance';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { renderSkeletonCards } from '../../../../components/SkeletonElements';
import { useIotsApi } from '../../../../contexts/iotsApi';
import { useLanguage } from '../../../../contexts/language';
import DeleteInsuranceConfirmationDialog from '../../Contracts/Insurances/DeleteInsuranceConfirmationDialog';
import InsuranceCard from '../../../../components/InsuranceCard';
import {
  ListElement,
  UseDraftListResult,
} from '../../../../hooks/useDraftList';
import { Vessel } from '../../../../types/vessel';
import useApiErrorMessageGetter from '../../../../hooks/useApiErrorMessage';
import AddButtonCard from '../../../../components/AddButtonCard';

interface InsuranceTabContentProps {
  vessel: Vessel;
  draftListManager: UseDraftListResult<VesselInsurance>;
  emptyContent: ReactNode;
  showAddButton?: boolean;
  areInsurancesPending: boolean;
  isFetchingInsurances: boolean;
  onInsurancesDataUpdate: () => void;
}

const InsuranceTabContent = ({
  areInsurancesPending,
  draftListManager,
  isFetchingInsurances,
  onInsurancesDataUpdate,
  vessel,
  emptyContent,
  showAddButton = false,
}: InsuranceTabContentProps) => {
  const { t } = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { addVesselInsurance, deleteVesselInsurance } = useIotsApi();

  const [insuranceListElementToDelete, setInsuranceListElementToDelete] =
    useState<ListElement<VesselInsurance> | null>(null);

  const {
    mutate: applyAddInsuranceMutation,
    isPending: isAddInsurancePending,
    reset: resetAddInsuranceMutation,
  } = useMutation({
    mutationFn: (newInsurance: NewVesselInsurance) =>
      addVesselInsurance(vessel.id, newInsurance),
  });

  const {
    mutate: applyDeleteInsuranceMutation,
    isPending: isDeleteInsurancePending,
    reset: resetDeleteInsuranceutation,
  } = useMutation({
    mutationFn: (insuranceId: string) =>
      deleteVesselInsurance(vessel.id, insuranceId),
  });

  if (areInsurancesPending) {
    return <div className="flex flex-col gap-4">{renderSkeletonCards(3)}</div>;
  }

  const { list, isReady, operations } = draftListManager;

  if (!list.length) {
    return (
      <>
        {emptyContent}
        {showAddButton && (
          <AddButtonCard
            disabled={!isReady}
            onClick={() => operations.addDraftElement()}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {showAddButton && (
        <AddButtonCard
          disabled={!isReady}
          onClick={() => operations.addDraftElement()}
        />
      )}
      {list.map(item =>
        item.isDraft ? (
          <InsuranceCard
            insuranceType="vessel"
            insurance={null}
            isLoading={isFetchingInsurances || isAddInsurancePending}
            onDelete={hasPendingChanges => {
              if (hasPendingChanges) {
                setInsuranceListElementToDelete(item);
              } else {
                operations.removeElement(item);
              }
            }}
            onSave={newInsurance => {
              applyAddInsuranceMutation(newInsurance, {
                onSuccess: insurance => {
                  operations.promoteDraftToPersistentElement(
                    item.id,
                    insurance,
                  );
                  enqueueSnackbar({
                    variant: 'success',
                    message: t('content.insurance-creation-success'),
                  });
                  onInsurancesDataUpdate();
                },
                onError: error => {
                  enqueueSnackbar({
                    variant: 'error',
                    message: getErrorMessage(
                      error,
                      t('content.insurance-creation-error'),
                    ),
                  });
                  resetAddInsuranceMutation();
                },
              });
            }}
            key={item.id}
          />
        ) : (
          <InsuranceCard
            insuranceType="vessel"
            insurance={item.data}
            isLoading={isFetchingInsurances || isAddInsurancePending}
            onSave={modifiedInsuranceValues => {
              // TODO: Implement edition
              console.log('Save changes to insurance', modifiedInsuranceValues);
            }}
            onDelete={() => {
              setInsuranceListElementToDelete(item);
            }}
            key={item.data.id}
          />
        ),
      )}
      <DeleteInsuranceConfirmationDialog
        insuranceItem={insuranceListElementToDelete}
        isLoading={isDeleteInsurancePending}
        onClose={() => {
          if (!isDeleteInsurancePending) {
            setInsuranceListElementToDelete(null);
          }
        }}
        onConfirm={() => {
          if (!insuranceListElementToDelete) {
            return;
          }
          if (insuranceListElementToDelete.isDraft) {
            operations.removeElement(insuranceListElementToDelete);
            setInsuranceListElementToDelete(null);
            return;
          }

          const { data: insurance } = insuranceListElementToDelete;
          applyDeleteInsuranceMutation(insurance.id, {
            onSuccess: () => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.insurance-deletion-success'),
              });
              operations.removeElement(insuranceListElementToDelete);
              setInsuranceListElementToDelete(null);
              onInsurancesDataUpdate();
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.insurance-deletion-error'),
                ),
              });
              resetDeleteInsuranceutation();
            },
          });
        }}
      />
    </div>
  );
};

export default InsuranceTabContent;
